import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

// Diagrams live in a ~300px chat column. Letting Mermaid shrink the SVG to fit
// (useMaxWidth, its default) scales the drawing but NOT the foreignObject label
// boxes, so wrapped node text gets clipped mid-line. Instead we render at natural
// size and let the container scroll — and scale small diagrams UP to MIN_WIDTH so
// they are actually readable.
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    curve: 'basis',
    // Native SVG <text>/<tspan> labels, NOT html-in-<foreignObject>. Mermaid measures
    // native text reliably and sizes each node rect around it, so labels neither get
    // clipped (the foreignObject box staying put while the SVG scaled) nor overflow
    // the box (what forcing height:auto on that box caused).
    htmlLabels: false,
    useMaxWidth: false,   // no auto-shrink — this is what truncated the labels
    nodeSpacing: 45,
    rankSpacing: 55,
    padding: 14,
    wrappingWidth: 190,   // wrap sooner so nodes stay tall-and-narrow, not clipped
  },
  themeVariables: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '15px',
  },
});

// Floor for the rendered width, in px. A 2-3 node diagram would otherwise come out
// postage-stamp sized next to the surrounding text.
const MIN_WIDTH = 460;
// Ceiling so a sprawling diagram stays scrollable rather than enormous.
const MAX_WIDTH = 1100;
// Below this scale the label text gets too small to read, so we stop shrinking and
// scroll instead. ~0.75 keeps a 15px font at ~11px.
const LEGIBLE_SCALE = 0.75;

let diagramId = 0;

// Mermaid hardcodes sizing onto the <svg> it returns. Rewrite it so the diagram
// renders at its natural aspect ratio, never below MIN_WIDTH, and never clipped.
function fitSvg(container) {
  const svg = container.querySelector('svg');
  if (!svg) return;

  const box = svg.viewBox && svg.viewBox.baseVal;
  const naturalW = (box && box.width) || svg.getBoundingClientRect().width;
  const naturalH = (box && box.height) || svg.getBoundingClientRect().height;
  if (!naturalW || !naturalH) return;

  // Scaling the whole SVG via width+height (with the viewBox intact) is uniform, so
  // labels shrink with the drawing and never clip — unlike the old max-width rule,
  // which changed width without height. So we MAY shrink to fit the column, but only
  // while the text stays legible; past that, stay big and let the container scroll.
  const avail = container.clientWidth - 24;   // minus .mermaid-diagram padding
  const shrinkToFit = avail > 0 ? avail / naturalW : 1;

  let width;
  if (shrinkToFit >= 1) {
    width = Math.min(Math.max(naturalW, MIN_WIDTH), MAX_WIDTH);   // upscale small ones
  } else if (shrinkToFit >= LEGIBLE_SCALE) {
    width = avail;                                                // a mild shrink reads fine
  } else {
    width = Math.min(naturalW, MAX_WIDTH);                        // too small to read — scroll
  }
  const height = naturalH * (width / naturalW);

  svg.style.maxWidth = 'none';          // overrides Mermaid's inline max-width
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
}

// Normalize LLM-generated mermaid code before handing it to the renderer.
function normalizeMermaidCode(raw) {
  let code = raw;
  // 1. Fix literal \n sequences → real newlines (double-escaped by JSON round-trip)
  code = code.replace(/\\n/g, '\n');
  // 2. Normalize line endings
  code = code.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // 3. Strip markdown code fences the LLM sometimes wraps the code in
  code = code.replace(/^```(?:mermaid)?\s*\n?/i, '').replace(/\n?```\s*$/, '');
  // 4. Remove Mermaid init directives (%%{init:…}%%) — can conflict with our config
  code = code.replace(/^%%\{[\s\S]*?%%\s*\n?/m, '');
  // 5. Inside a quoted node label a raw newline is not a line break for Mermaid —
  //    it wants <br/>. Models emit both, so normalize to the one that works.
  code = code.replace(/"([^"]*)"/g, function (m, inner) {
    return '"' + inner.replace(/\s*\n\s*/g, '<br/>') + '"';
  });
  // 6. Trim leading/trailing whitespace
  code = code.trim();
  return code;
}

function MermaidDiagram({ code }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const render = async () => {
      diagramId += 1;
      const id = `mermaid-diagram-${diagramId}`;
      const normalized = normalizeMermaidCode(code);
      try {
        const { svg } = await mermaid.render(id, normalized);
        if (cancelled) return;
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          fitSvg(containerRef.current);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Mermaid render failed:', err);
        setError(err.message || String(err));
      }
    };

    render();
    return () => { cancelled = true; };
  }, [code]);

  if (error) {
    const normalized = normalizeMermaidCode(code);
    return (
      <div className="mermaid-error">
        <div className="mermaid-error-label">Diagram failed to render:</div>
        <pre>{normalized}</pre>
      </div>
    );
  }

  return <div className="mermaid-diagram" ref={containerRef} />;
}

export default MermaidDiagram;
